import { useEffect, useState, useCallback } from "react"
import { getBankAccounts, verifyBankAccount, removeBankAccount } from "@/services/payout.service"
import { useToast } from "@/context/ToastContext"
import { Badge } from "@/components/common/Badge"
import { Button } from "@/components/common/Button"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow } from "@/components/common/Spinner"
import { RefreshCw, CheckCircle, Trash2, ShieldCheck } from "lucide-react"
import type { BankAccount } from "@/types"

type BankWithUser = BankAccount & { user_email: string; user_name: string | null }

export function BankAccountsPage() {
  const { toast } = useToast()
  const [accounts, setAccounts] = useState<BankWithUser[]>([])
  const [loading, setLoading]   = useState(true)
  const [acting, setActing]     = useState<string | null>(null)
  const [filter, setFilter]     = useState("all")

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getBankAccounts()
      setAccounts(data)
    } catch { toast("Failed to load bank accounts", "error") }
    finally { setLoading(false) }
  }, [toast])

  useEffect(() => { load() }, [load])

  const filtered = filter === "all" ? accounts : accounts.filter(a => a.status === filter)

  const pendingCount   = accounts.filter(a => a.status === "pending").length
  const verifiedCount  = accounts.filter(a => a.status === "verified").length

  const handleVerify = async (a: BankWithUser) => {
    setActing(a.id)
    try {
      await verifyBankAccount(a.id)
      toast(`Bank account verified for ${a.user_email}`, "success")
      load()
    } catch { toast("Failed to verify account", "error") }
    finally { setActing(null) }
  }

  const handleRemove = async (a: BankWithUser) => {
    setActing(a.id)
    try {
      await removeBankAccount(a.id)
      toast("Bank account removed", "success")
      load()
    } catch { toast("Failed to remove account", "error") }
    finally { setActing(null) }
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Accounts",  value: accounts.length, color: "bg-zimrah-500" },
          { label: "Pending Review",  value: pendingCount,    color: "bg-amber-500"  },
          { label: "Verified",        value: verifiedCount,   color: "bg-green-500"  },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className={`h-1 ${s.color}`} />
            <div className="p-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">{s.label}</p>
              <p className="text-3xl font-black text-gray-900">{loading ? "—" : s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zimrah-500 transition-all">
          <option value="all">All Accounts</option>
          <option value="pending">Pending</option>
          <option value="verified">Verified</option>
          <option value="failed">Failed</option>
          <option value="removed">Removed</option>
        </select>
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
        {pendingCount > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
            <ShieldCheck size={14} className="text-amber-600" />
            <span className="text-xs font-bold text-amber-700">{pendingCount} accounts awaiting verification</span>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {["Member", "Bank", "Account Type", "Routing (last 4)", "Account (last 4)", "Primary", "Status", "Actions"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <LoadingRow cols={8} /> :
               filtered.length === 0 ? (
                <tr><td colSpan={8}>
                  <EmptyState icon="🏦" title="No bank accounts" subtitle="Member bank accounts will appear here." />
                </td></tr>
               ) : filtered.map(a => (
                <tr key={a.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-bold text-sm text-gray-900">{a.user_name ?? "—"}</p>
                    <p className="text-xs text-gray-400">{a.user_email}</p>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {a.bank_name ?? "—"}
                    <p className="text-xs text-gray-400">{a.account_holder_name}</p>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="info">{a.account_type}</Badge>
                  </td>
                  <td className="px-6 py-4 font-mono text-sm text-gray-600">
                    ••••{a.routing_number_last4}
                  </td>
                  <td className="px-6 py-4 font-mono text-sm text-gray-600">
                    ••••{a.account_number_last4}
                  </td>
                  <td className="px-6 py-4">
                    {a.is_primary ? <Badge variant="purple">Primary</Badge> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-6 py-4">
                    {a.status === "verified" ? <Badge variant="success" dot>Verified</Badge>  :
                     a.status === "pending"  ? <Badge variant="warning" dot>Pending</Badge>   :
                     a.status === "failed"   ? <Badge variant="danger"  dot>Failed</Badge>    :
                     <Badge variant="muted" dot>Removed</Badge>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1">
                      {a.status === "pending" && (
                        <Button size="sm" variant="success" icon={<CheckCircle size={13} />}
                          loading={acting === a.id}
                          onClick={() => handleVerify(a)}>
                          Verify
                        </Button>
                      )}
                      {a.status !== "removed" && (
                        <Button size="sm" variant="ghost" icon={<Trash2 size={13} />}
                          loading={acting === a.id}
                          onClick={() => handleRemove(a)}>
                          Remove
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
               ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}